import { asDecimal } from '@/lib/helpers/numeric'

/**
 * Conversion factors to meters (base unit)
 */
const TO_METERS = {
    cm: 0.01,
    m: 1,
    km: 1000,
    in: 0.0254,
    ft: 0.3048,
    mi: 1609.34,
}

/**
 * Convert any distance unit to meters
 */
const toMeters = (value, unit) => value * (TO_METERS[unit] || 1)

/**
 * Convert meters to any distance unit
 */
const fromMeters = (meters, unit) => meters / (TO_METERS[unit] || 1)

/**
 * Auto-scale metric distance to appropriate unit
 */
export const autoScaleMetric = (distance) => {
    const absValue = Math.abs(distance.value)

    if (distance.unit === 'cm') {
        if (absValue >= 100) {
            distance.value /= 100
            distance.unit = 'm'
        }
    }

    if (distance.unit === 'm') {
        if (absValue < 1) {
            distance.value *= 100
            distance.unit = 'cm'
        } else if (absValue >= 1000) {
            distance.value /= 1000
            distance.unit = 'km'
        }
    }

    if (distance.unit === 'km') {
        if (absValue < 1) {
            distance.value *= 1000
            distance.unit = 'm'
            if (Math.abs(distance.value) < 1) {
                distance.value *= 100
                distance.unit = 'cm'
            }
        }
    }

    return distance
}

/**
 * Auto-scale imperial distance to appropriate unit
 */
export const autoScaleImperial = (distance) => {
    const absValue = Math.abs(distance.value)

    if (distance.unit === 'in') {
        if (absValue >= 12) {
            distance.value /= 12
            distance.unit = 'ft'
        }
    }

    if (distance.unit === 'ft') {
        if (absValue < 1) {
            distance.value *= 12
            distance.unit = 'in'
        } else if (absValue >= 5280) {
            distance.value /= 5280
            distance.unit = 'mi'
        }
    }

    if (distance.unit === 'mi') {
        if (absValue < 1) {
            distance.value *= 5280
            distance.unit = 'ft'
            if (Math.abs(distance.value) < 1) {
                distance.value *= 12
                distance.unit = 'in'
            }
        }
    }

    return distance
}

/**
 * Determine if unit is metric
 */
const isMetric = (unit) => ['cm', 'm', 'km'].includes(unit)

/**
 * Convert distance between units
 *
 * @param {number} value - Value to convert
 * @param {string} unitIn - Source unit
 * @param {string} unitOut - Target unit
 * @param {boolean} forceUnit - If true, don't auto-scale
 * @param {number|null} decimals - Number of decimals (null to disable)
 * @param {boolean} returnString - If true, return formatted string
 * @returns {object|string}
 */
export const convertDistance = (
    value,
    unitIn = 'm',
    unitOut = 'm',
    forceUnit = false,
    decimals = 2,
    returnString = false
) => {
    unitOut = unitOut ?? 'm'

    // Convert to meters first, then to target unit
    const meters = toMeters(value, unitIn)
    let distance = {
        value: fromMeters(meters, unitOut),
        unit: unitOut,
    }

    // Auto-scale if not forced
    if (!forceUnit) {
        if (isMetric(unitOut)) {
            distance = autoScaleMetric(distance)
        } else {
            distance = autoScaleImperial(distance)
        }
    }

    if (decimals !== null) {
        distance.value = asDecimal(distance.value, decimals)
    }

    return returnString ? `${distance.value} ${distance.unit}` : distance
}

// Legacy helper functions for backwards compatibility
export const cmToMorKm = (distance) => autoScaleMetric({ ...distance })
export const mToCmOrKm = (distance) => autoScaleMetric({ ...distance })
export const kmToMorCm = (distance) => autoScaleMetric({ ...distance })
export const miToFtOrIn = (distance) => autoScaleImperial({ ...distance })
export const ftToInOrMi = (distance) => autoScaleImperial({ ...distance })
export const inToFtOrMi = (distance) => autoScaleImperial({ ...distance })
